import localforage from 'localforage';
import { create } from 'zustand';
import { combine, createJSONStorage, devtools, persist } from 'zustand/middleware';
import { getMyInfo, mapAuthUser } from '@/api/api.login';
import { tokenManager } from '@/utils/token';

/**
 * 用户数据类型
 */
export type User = {
  /** 用户ID（后端返回） */
  id: number;
  /** 昵称（后端返回，可能为空） */
  nickname: string | null;
  /** 分组/组织 */
  group: string;
  /** 手机号 */
  phone: string;
  /** 设备标识（IMEI/设备指纹等，后端返回，可能为空） */
  imei: string | null;
  /** 用户状态 */
  status: string;
  /** 是否允许设备重绑 */
  allowRebind: boolean;
  /** 头像（URL，可能为空） */
  avatar: string | null;
  /** VIP 到期时间（可能为空） */
  vipEndedAt: string | null;
  /** 通知设置/扩展字段 */
  notification: Record<string, unknown>;
  /** 原始短信摘要字段 */
  summary?: {
    sms?: {
      imei?: string | null;
      phone?: string | null;
    };
  };
};

/**
 * 用户状态类型
 */
type UserState = {
  /** 当前用户信息 */
  user: User | null;
  /** 是否为有效 VIP */
  isVipValid: boolean;
  /** 持久化状态是否已完成恢复 */
  hasHydrated: boolean;
  /** 设置用户信息 */
  setUser: (user: User) => void;
  /** 清除用户信息（退出登录） */
  clearUser: () => void;
  /** 标记持久化恢复完成 */
  setHasHydrated: (hydrated: boolean) => void;
  /** 刷新 VIP 有效状态 */
  refreshVipValid: () => boolean;
  /** 主动拉取 my_info 并同步用户信息 */
  refreshUserInfo: () => Promise<boolean>;
  /**
   * 退出登录
   * - 清理 token
   * - 清理 user
   */
  logout: () => Promise<void>;
};

/**
 * 根据 VIP 到期时间判断是否有效
 * @param vipEndedAt - VIP 到期时间
 */
const calcVipValid = (vipEndedAt: string | null) => {
  if (!vipEndedAt) return false;
  const date = new Date(vipEndedAt);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() >= Date.now();
};

/**
 * 用户状态管理 Store
 * 使用 localforage 持久化存储用户信息
 */
export const useUserStore = create<UserState>()(
  persist(
    /** devtools 中间件 */
    devtools(
      /** 函数自动类型推断中间件 */
      combine(
        {
          user: null as User | null,
          isVipValid: false as boolean,
          hasHydrated: false as boolean,
        },
        (set, get) => {
          let pendingRefreshUserInfo: Promise<boolean> | null = null;

          return {
            setUser: (user) => {
              set({ user, isVipValid: calcVipValid(user.vipEndedAt) });
            },

            clearUser: () => {
              set({ user: null, isVipValid: false });
            },

            setHasHydrated: (hydrated) => {
              set({ hasHydrated: hydrated });
            },

            refreshVipValid: () => {
              const user = get().user;
              const isVipValid = calcVipValid(user?.vipEndedAt ?? null);
              set({ isVipValid });
              return isVipValid;
            },

            refreshUserInfo: async () => {
              if (pendingRefreshUserInfo) {
                return pendingRefreshUserInfo;
              }

              const user = get().user;
              if (!user) {
                return false;
              }

              pendingRefreshUserInfo = (async () => {
                const response = await getMyInfo();
                if (response.code !== 0 || !response.data) {
                  await tokenManager.clearAccessToken();
                  set({ user: null, isVipValid: false });
                  return false;
                }

                await tokenManager.setAccessToken(response.data.token);
                set({
                  user: mapAuthUser(response.data, user.phone),
                  isVipValid: calcVipValid(response.data.vipEndedAt ?? null),
                });
                return true;
              })();

              try {
                return await pendingRefreshUserInfo;
              } finally {
                pendingRefreshUserInfo = null;
              }
            },

            logout: async () => {
              await tokenManager.clearAccessToken();
              set({ user: null, isVipValid: false });
            },
          };
        },
      ),
      { name: 'userStore' },
    ),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => localforage),
      onRehydrateStorage: () => (state) => {
        state?.refreshVipValid();
        state?.setHasHydrated(true);
      },
    },
  ),
);
